// Classic trainers (no electronics): "virtual" power estimated from the rear wheel speed.
// A fluid, magnetic or air roller offers a resistance that depends only on speed: P = a·v + b·v² + c·v³.
// The brand curves are those published by the manufacturers (reused by GoldenCheetah and TrainerRoad);
// the generic curves are orders of magnitude. Expected accuracy: ±10% on a well-tightened roller.

export type TrainerCurveKind = 'fluid' | 'magnetic' | 'air';

export interface TrainerCurve {
  id: string;
  /** Commercial name (untranslated); absent for generic curves, named by the UI. */
  model?: string;
  kind: TrainerCurveKind;
  /** Speed unit of the published coefficients. */
  unit: 'kmh' | 'mph';
  /** Coefficients a, b, c of P = a·v + b·v² + c·v³ (W). */
  coef: [number, number, number];
}

export const TRAINER_CURVES: readonly TrainerCurve[] = [
  { id: 'generic-fluid', kind: 'fluid', unit: 'kmh', coef: [3.26, 0, 0.0046] },
  { id: 'generic-magnetic', kind: 'magnetic', unit: 'kmh', coef: [4, 0.09, 0] },
  { id: 'generic-air', kind: 'air', unit: 'kmh', coef: [0, 0, 0.0074] },
  { id: 'kurt-road-machine', model: 'Kinetic Road Machine / Rock and Roll', kind: 'fluid', unit: 'mph', coef: [5.24482, 0, 0.019168] },
  { id: 'kurt-cyclone', model: 'Kinetic Cyclone', kind: 'fluid', unit: 'mph', coef: [6.48109, 0, 0.020106] },
  { id: 'cycleops-fluid2', model: 'CycleOps / Saris Fluid2', kind: 'fluid', unit: 'mph', coef: [8.9788, -0.0137, 0.0115] },
];

export const trainerCurve = (id: string | null | undefined): TrainerCurve | null => TRAINER_CURVES.find(c => c.id === id) ?? null;

/** Beyond this, the measured speed is a sensor error (wheel miscounted, magnet shifted). */
export const MAX_WHEEL_SPEED_KMH = 100;
const MAX_POWER = 2500;
const MPH = 1.609344;

/** Estimated power (W) for a wheel speed (km/h). Always bounded to 0–2500 W. */
export function virtualPower(curve: TrainerCurve, speedKmh: number): number {
  if (!Number.isFinite(speedKmh) || speedKmh <= 0 || speedKmh > MAX_WHEEL_SPEED_KMH) return 0;
  const v = curve.unit === 'mph' ? speedKmh / MPH : speedKmh;
  const [a, b, c] = curve.coef;
  return Math.max(0, Math.min(MAX_POWER, a * v + b * v * v + c * v * v * v));
}

/** Common wheel circumferences (mm), for setting up a speed sensor. */
/** The name is the tyre designation, the same in every language. */
export const WHEEL_SIZES: readonly { name: string; mm: number }[] = [
  { name: '700×23C', mm: 2096 },
  { name: '700×25C', mm: 2105 },
  { name: '700×28C', mm: 2136 },
  { name: '700×32C', mm: 2155 },
  { name: '650B×47', mm: 2079 },
  { name: '26″', mm: 2068 },
  { name: '27.5″', mm: 2148 },
  { name: '29″', mm: 2288 },
];
export const DEFAULT_WHEEL_MM = 2105;
export const clampWheel = (mm: number) => (Number.isFinite(mm) ? Math.round(Math.max(1000, Math.min(2500, mm))) : DEFAULT_WHEEL_MM);
